import { Link } from 'react-router-dom'
import { Trophy, Zap, Shield, Gift, ChevronRight, Check } from 'lucide-react'

export default function LandingPage() {
  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-kiniela-bg/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img 
              src="/kiniela_logo.png" 
              alt="Kiniela.mx Logo" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-2xl font-bold text-kiniela-gold">Kiniela.mx</span>
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/login" className="btn bg-kiniela-card text-kiniela-text hover:bg-[#333333] border border-kiniela-border">
              Iniciar Sesión
            </Link>
            <Link to="/register" className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover">
              Registrarse
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-kiniela-bg via-kiniela-accent to-kiniela-bg text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Demuestra tu Conocimiento
            <br />
            <span className="text-kiniela-gold">Gana Premios</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-kiniela-text max-w-3xl mx-auto">
            Participa en quinielas de fútbol, haz tus pronósticos y compite por los primeros lugares.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover py-4 px-8 text-lg font-semibold inline-flex items-center justify-center"
            >
              Comenzar Ahora
              <ChevronRight className="ml-2" size={20} />
            </Link>
            <Link 
              to="/quinielas" 
              className="btn bg-kiniela-accent text-kiniela-text hover:bg-kiniela-accentHover py-4 px-8 text-lg font-semibold border border-kiniela-border"
            >
              Ver Quinielas
            </Link>
          </div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-kiniela-card/50 backdrop-blur-sm rounded-lg p-6 border border-kiniela-border">
              <div className="text-4xl font-bold text-kiniela-gold mb-2">$50</div>
              <div className="text-kiniela-text">Mínimo para jugar</div>
            </div>
            <div className="bg-kiniela-card/50 backdrop-blur-sm rounded-lg p-6 border border-kiniela-border">
              <div className="text-4xl font-bold text-kiniela-gold mb-2">Top 3</div>
              <div className="text-kiniela-text">Lugares premiados</div>
            </div>
            <div className="bg-kiniela-card/50 backdrop-blur-sm rounded-lg p-6 border border-kiniela-border">
              <div className="text-4xl font-bold text-kiniela-gold mb-2">90%</div>
              <div className="text-kiniela-text">Para premios</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-kiniela-bg">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4 text-kiniela-gold">¿Cómo Funciona?</h2>
          <p className="text-xl text-kiniela-text text-center mb-12">Simple, rápido y seguro</p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '1',
                title: 'Regístrate',
                description: 'Crea tu cuenta en menos de 2 minutos',
                icon: '👤',
              },
              {
                step: '2',
                title: 'Deposita',
                description: 'Agrega saldo con transferencia',
                icon: '💳',
              },
              {
                step: '3',
                title: 'Pronostica',
                description: 'Selecciona quiniela y haz tus predicciones',
                icon: '⚽',
              },
              {
                step: '4',
                title: 'Gana',
                description: 'Acumula puntos y retira tus premios',
                icon: '🏆',
              },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="bg-kiniela-card w-16 h-16 rounded-full flex items-center justify-center text-3xl mx-auto mb-4 border border-kiniela-border">
                  {item.icon}
                </div>
                <div className="text-sm font-semibold text-kiniela-gold mb-2">Paso {item.step}</div>
                <h3 className="text-xl font-bold mb-2 text-kiniela-text">{item.title}</h3>
                <p className="text-kiniela-text">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-kiniela-card">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-kiniela-gold">¿Por qué Kiniela.mx?</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card bg-kiniela-bg border border-kiniela-border text-center">
              <div className="bg-kiniela-accent/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-kiniela-border">
                <Zap className="text-kiniela-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-kiniela-text">Rápido y Fácil</h3>
              <p className="text-kiniela-text">
                Interfaz intuitiva para hacer tus pronósticos en segundos
              </p>
            </div>

            <div className="card bg-kiniela-bg border border-kiniela-border text-center">
              <div className="bg-kiniela-cta/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-kiniela-cta">
                <Shield className="text-kiniela-cta" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-kiniela-text">100% Seguro</h3>
              <p className="text-kiniela-text">
                Tus datos y dinero protegidos con tecnología de última generación
              </p>
            </div>

            <div className="card bg-kiniela-bg border border-kiniela-border text-center">
              <div className="bg-kiniela-gold/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 border border-kiniela-gold">
                <Gift className="text-kiniela-gold" size={32} />
              </div>
              <h3 className="text-xl font-bold mb-3 text-kiniela-text">Grandes Premios</h3>
              <p className="text-kiniela-text">
                90% del total recaudado se distribuye entre los ganadores
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-kiniela-bg">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-kiniela-gold">Preguntas Frecuentes</h2>

          <div className="space-y-6">
            {[
              {
                q: '¿Cómo puedo depositar dinero?',
                a: 'Aceptamos depósitos por CLABE bancaria.',
              },
              {
                q: '¿Cuánto tiempo tengo para hacer mis pronósticos?',
                a: 'Puedes hacer tus pronósticos hasta 1 hora antes del inicio del primer partido de la jornada.',
              },
              {
                q: '¿Puedo modificar mis pronósticos?',
                a: 'No, una vez guardados no pueden modificarse. Asegúrate de revisarlos antes de confirmar.',
              },
              {
                q: '¿Cómo se calculan los ganadores?',
                a: 'Se suman los puntos de aciertos. Los 3 primeros lugares se llevan el premio.',
              },
              {
                q: '¿Puedo retirar mi dinero depositado?',
                a: 'Solo puedes retirar dinero ganado. El dinero depositado debe usarse para jugar.',
              },
            ].map((faq, index) => (
              <details key={index} className="group bg-kiniela-card rounded-lg p-6 cursor-pointer border border-kiniela-border">
                <summary className="font-semibold text-lg flex items-start justify-between text-kiniela-text">
                  <span>{faq.q}</span>
                  <ChevronRight className="group-open:rotate-90 transition-transform flex-shrink-0 ml-4 text-kiniela-gold" />
                </summary>
                <p className="mt-4 text-kiniela-text">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-kiniela-card border-t-4 border-kiniela-gold">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-kiniela-gold">¿Listo para Ganar?</h2>
          <p className="text-xl mb-8 text-kiniela-muted">
            Únete a miles de usuarios que ya están demostrando su conocimiento del fútbol
          </p>
          <Link to="/register" className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover py-4 px-8 text-lg font-semibold inline-flex items-center">
            Crear Cuenta Gratis
            <ChevronRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-kiniela-bg text-kiniela-text py-8 border-t border-kiniela-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm">
            &copy; 2026 Kiniela.mx. Todos los derechos reservados. Juego responsable +18.
          </p>
          <div className="mt-4 space-x-4">
            <Link to="/terminos" className="text-sm hover:text-kiniela-gold">Términos</Link>
            <Link to="/privacy" className="text-sm hover:text-kiniela-gold">Privacidad</Link>
            <a href="mailto:soporte@kiniela.mx" className="text-sm hover:text-kiniela-gold">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
